import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthToken } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

interface TeamStats {
  teamId: string;
  total_points: number;
  weekly_admissions: number;
  monthly_admissions: number;
  annual_admissions: number;
  all_time_admissions: number;
  isTeamOfTheWeek?: boolean;
}

const TEAM_COLORS: Record<string, string> = {
  blue: '#2563eb',
  green: '#16a34a',
  red: '#dc2626',
  yellow: '#ca8a04',
  orange: '#ea580c'
};

const TeamCompetitionBoard: React.FC = () => {
  const [teams, setTeams] = useState<TeamStats[]>([]);
  const [teamOfTheWeek, setTeamOfTheWeek] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamCompetition();
  }, []);

  const fetchTeamCompetition = async () => {
    try {
      const token = await getAuthToken();
      const response = await axios.get(`${API_URL}/admissions-game/team-competition`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTeams(response.data.data.teams);
      setTeamOfTheWeek(response.data.data.teamOfTheWeek);
    } catch (error) {
      console.error('Error fetching team competition:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading team competition...</div>;
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Team Competition Board</h2>
      
      {/* Team of the Week Banner */}
      {teamOfTheWeek && (
        <div 
          className="mb-6 p-4 rounded-lg text-white font-bold text-center"
          style={{ backgroundColor: TEAM_COLORS[teamOfTheWeek] }}
        >
          🏆 Team of the Week: {teamOfTheWeek.toUpperCase()} 🏆
        </div>
      )}
      
      {/* Team Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {teams.map((team) => (
          <div
            key={team.teamId}
            className="border rounded-lg p-4 shadow-sm"
            style={{ borderLeft: `4px solid ${TEAM_COLORS[team.teamId]}` }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 
                className="text-xl font-bold uppercase"
                style={{ color: TEAM_COLORS[team.teamId] }}
              >
                {team.teamId}
              </h3>
              {team.isTeamOfTheWeek && <span className="text-2xl">🏆</span>}
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Weekly Admissions:</span>
                <span className="font-bold">{team.weekly_admissions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Monthly Admissions:</span>
                <span className="font-bold">{team.monthly_admissions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Annual Admissions:</span>
                <span className="font-bold">{team.annual_admissions}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-600">All-Time:</span>
                <span className="font-bold text-lg">{team.all_time_admissions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Points:</span>
                <span className="font-bold">{team.total_points}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeamCompetitionBoard;
